
import { useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Coupon } from '@/types/admin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

interface AddCouponFormProps {
  onSubmit: (data: any) => void;
  onCancel: () => void;
  initialData?: Coupon | null;
}

const couponSchema = z.object({
  code: z.string().min(3, { message: 'Coupon code must be at least 3 characters' }).max(20, { message: 'Coupon code must be less than 20 characters' }),
  discount_type: z.enum(['percentage', 'fixed']),
  discount_value: z.number().positive({ message: 'Discount value must be positive' }),
  active: z.boolean(),
  single_use_per_user: z.boolean(),
  coupon_type: z.enum(['Order', 'Booking'])
});

const AddCouponForm = ({ onSubmit, onCancel, initialData }: AddCouponFormProps) => {
  const form = useForm<z.infer<typeof couponSchema>>({
    resolver: zodResolver(couponSchema),
    defaultValues: {
      code: initialData?.code || '',
      discount_type: initialData?.discount_type || 'percentage',
      discount_value: initialData?.discount_value || 10,
      active: initialData?.active ?? true,
      single_use_per_user: initialData?.single_use_per_user ?? false,
      coupon_type: initialData?.coupon_type || 'Order'
    }
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        code: initialData.code,
        discount_type: initialData.discount_type,
        discount_value: initialData.discount_value,
        active: initialData.active,
        single_use_per_user: initialData.single_use_per_user,
        coupon_type: initialData.coupon_type
      });
    }
  }, [initialData, form]);

  const handleSubmit = (values: z.infer<typeof couponSchema>) => {
    if (initialData) {
      onSubmit({ ...values, id: initialData.id, created_at: initialData.created_at });
    } else {
      onSubmit(values);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 max-w-2xl mx-auto">
      <h2 className="text-xl font-semibold mb-6">
        {initialData ? 'Edit Coupon' : 'Add New Coupon'}
      </h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Coupon Code</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="SUMMER20" />
                  </FormControl>
                  <FormDescription>
                    Enter a unique code for the coupon
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="discount_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Discount Type</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select discount type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Choose whether the discount is a percentage or fixed amount
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="discount_value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Discount Value</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      {...field}
                      onChange={(e) => field.onChange(parseFloat(e.target.value))}
                      min={0}
                    />
                  </FormControl>
                  <FormDescription>
                    {form.watch('discount_type') === 'percentage' 
                      ? 'Enter percentage value (e.g., 20 for 20%)' 
                      : 'Enter fixed amount in rupees'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="coupon_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Coupon Type</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select coupon type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Order">Order</SelectItem>
                      <SelectItem value="Booking">Booking</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Choose whether this coupon applies to orders or bookings
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Active</FormLabel>
                    <FormDescription>
                      Enable or disable this coupon
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="single_use_per_user"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Single Use Per User</FormLabel>
                    <FormDescription>
                      Limit coupon to one-time use per user
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit">
              {initialData ? 'Update Coupon' : 'Create Coupon'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AddCouponForm;
